import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES } from '../kanjinData'

export interface IchijoMember {
  name: string
  /** 対応する寄進地（kanjinData SITES の no）。演出のキャプション・発射元はこれで照合する */
  siteNo: number
  boxes: { size: [number, number, number]; pos: [number, number, number] }[]
  color?: number
}

// 12部材（12古材に対応。寸法は実寸スケール・簡略）。Proto11 と共有
export const ICHIJO_MEMBERS: IchijoMember[] = [
    { name: '畳の額縁 — 後醍醐天皇の御垢附板', siteNo: 38, boxes: [
      { size: [1.06, 0.04, 0.08], pos: [0, 0.03, -0.95] }, { size: [1.06, 0.04, 0.08], pos: [0, 0.03, 0.95] },
      { size: [0.08, 0.04, 1.98], pos: [-0.49, 0.03, 0] }, { size: [0.08, 0.04, 1.98], pos: [0.49, 0.03, 0] } ] },
    { name: '床の間の釣柱 — 擬宝珠を逆さに', siteNo: 42, boxes: [{ size: [0.07, 1.85, 0.07], pos: [-0.35, 0.95, -0.85] }, { size: [0.14, 0.14, 0.14], pos: [-0.35, 1.7, -0.85] }], color: 0xd08a4a },
    { name: '白鳳の水城材 — 横木', siteNo: 16, boxes: [{ size: [0.8, 0.07, 0.07], pos: [0, 1.82, -0.85] }] },
    { name: '雲竜の天井', siteNo: 3, boxes: [{ size: [1.0, 0.02, 0.9], pos: [0, 1.92, -0.5] }], color: 0x7aa0c8 },
    { name: '金箔の羽目板 — 神棚', siteNo: 60, boxes: [{ size: [0.5, 0.42, 0.05], pos: [-0.22, 1.45, -0.88] }], color: 0xe8c56a },
    { name: '朱漆の南扉', siteNo: 53, boxes: [{ size: [0.62, 1.25, 0.04], pos: [0, 0.72, 0.93] }], color: 0xc0503a },
    { name: '五老松の板 — 欄間', siteNo: 74, boxes: [{ size: [0.9, 0.26, 0.04], pos: [0, 1.72, 0.6] }] },
    { name: 'ビャクシンの鴨居', siteNo: 54, boxes: [{ size: [0.55, 0.07, 0.06], pos: [0.22, 1.55, -0.35] }] },
    { name: '舞殿の木鼻', siteNo: 34, boxes: [{ size: [0.16, 0.12, 0.12], pos: [0.45, 1.6, 0.55] }] },
    { name: '法隆寺の棚板 — 書棚', siteNo: 85, boxes: [{ size: [0.46, 0.02, 0.32], pos: [0.44, 1.15, -0.55] }, { size: [0.46, 0.02, 0.32], pos: [0.44, 0.85, -0.55] }] },
    { name: '大鳥居の書棚戸', siteNo: 4, boxes: [{ size: [0.46, 0.5, 0.03], pos: [0.44, 0.55, -0.42] }] },
    { name: '弁慶楠の板 — そして畳、成る', siteNo: 84, boxes: [{ size: [0.91, 0.05, 1.82], pos: [0, 0.05, 0] }], color: 0x9fd08a },
]

/** 部材に対応する寄進地を返す（no照合・見つからなければ順送り） */
export function siteOfMember(i: number): (typeof SITES)[number] {
  return SITES.find((s) => s.no === ICHIJO_MEMBERS[i].siteNo) ?? SITES[i % SITES.length]
}

/** ⑧ 一畳敷フレーム実体化 — 木片が届くたび、実寸ワイヤーフレームの部材が1本ずつ実体化 */
export class Proto8 extends ProtoBase {
  readonly title = '8. 一畳敷フレーム実体化'
  readonly desc = '暗闇に浮かぶ実寸の一畳敷。到着した木片ごとに部材が実体化し、部屋が組み上がる'

  private members: { lines: THREE.LineSegments[]; revealed: boolean }[] = []
  private next = 0
  private launchTimer = 0
  private doneT = 0

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.setCaption('闇の中に、まだ形のない一畳がある——')
    this.ctx.view.setPose(new THREE.Vector3(2.7, 2.0, 3.6), new THREE.Vector3(0, 1.0, 0))

    // 淡い床
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(4, 40),
      new THREE.MeshBasicMaterial({ color: 0x141a22, transparent: true, opacity: 0.9 }),
    )
    floor.rotation.x = -Math.PI / 2
    this.group.add(floor)

    for (const m of ICHIJO_MEMBERS) {
      const set: THREE.LineSegments[] = []
      for (const b of m.boxes) {
        const geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(...b.size))
        const mat = new THREE.LineBasicMaterial({ color: m.color ?? 0xffd77a, transparent: true, opacity: 0 })
        const ls = new THREE.LineSegments(geo, mat)
        ls.position.set(...b.pos)
        this.group.add(ls)
        set.push(ls)
      }
      this.members.push({ lines: set, revealed: false })
    }
    this.launchTimer = 1.2
  }

  update(dt: number): void {
    this.elapsed += dt
    this.launchTimer -= dt

    if (this.next < this.members.length && this.launchTimer <= 0) {
      const i = this.next++
      this.launchTimer = 2.0
      const site = siteOfMember(i)
      const targetBox = ICHIJO_MEMBERS[i].boxes[0]
      const to = new THREE.Vector3(...targetBox.pos)
      const az = Math.random() * Math.PI * 2
      const from = new THREE.Vector3(Math.cos(az) * 22, 5 + Math.random() * 6, Math.sin(az) * 22)
      this.ctx.chips.launch({ from, to, dur: 1.6, apex: 4, site, scale: 0.35 })
      this.ctx.chips.onArrive = () => {
        const m = this.members[i]
        m.revealed = true
        this.ctx.sfx.play(i === this.members.length - 1 ? 'drum' : 'chime', to)
        this.ctx.setCaption(`${ICHIJO_MEMBERS[i].name}（${site.building}より）`)
      }
    }

    // フェードイン＋完成パルス
    this.members.forEach((m) => {
      for (const ls of m.lines) {
        const mat = ls.material as THREE.LineBasicMaterial
        if (m.revealed && mat.opacity < 1) mat.opacity = Math.min(1, mat.opacity + dt * 2.2)
      }
    })

    const allDone = this.members.every((m) => m.revealed)
    if (allDone && this.doneT === 0) {
      this.doneT = this.elapsed
      this.ctx.setCaption('一畳敷、成る——六十六人の勧進、八十九の木片')
    }
    if (this.doneT > 0) {
      const pulse = 0.75 + 0.25 * Math.sin((this.elapsed - this.doneT) * 3)
      this.members.forEach((m) => m.lines.forEach((ls) => ((ls.material as THREE.LineBasicMaterial).opacity = pulse)))
      if (this.elapsed - this.doneT > 7) {
        // ループ
        this.doneT = 0
        this.next = 0
        this.launchTimer = 1
        this.members.forEach((m) => {
          m.revealed = false
          m.lines.forEach((ls) => ((ls.material as THREE.LineBasicMaterial).opacity = 0))
        })
        this.ctx.chips.clear()
      }
    }
  }

  protected onDispose(): void {
    this.ctx.chips.onArrive = undefined
    this.ctx.chips.clear()
  }
}
